import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';
import type { MessageDescriptor } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Flex, Inline, xcss, Box } from '@atlaskit/primitives';
import Button, { IconButton } from '@atlaskit/button/new';
import ClockIconMigration from '@atlaskit/icon/core/clock';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import { token } from '@atlaskit/tokens';

import { SidePanel, SidePanelFooter } from '@confluence/side-panel';
import { useSidePanelStateActions } from '@confluence/side-panel-state-container';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import type { ContentTypeEventService } from '@confluence/content-types-bridge/entry-points/content-types-event-service.ts';
import { useVersionHistoryExperienceTracking } from '@confluence/content-types-utils/entry-points/useVersionHistoryExperienceTracking.ts';
import { fg } from '@confluence/feature-gating';

import { useVersionHistory } from './useVersionHistory.ts';
import { useVersionHistoryAnalytics } from './useVersionHistoryAnalytics.ts';
import { RestoreContentModal } from './RestoreContentModal.tsx';
import { VersionHistoryContent } from './VersionHistoryContent.tsx';
import type { useVersionHistoryContentVersionHistoryQuery_contentVersionHistory_ContentVersionHistoryConnection_nodes_ContentVersion as VersionHistoryNodeType } from './__types__/useVersionHistoryContentVersionHistoryQuery.ts';
import { VersionHistoryLoadingPlaceholder } from './VersionHistoryLoadingPlaceholder.tsx';

const headerTitleStyles = xcss({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
});

const i18n = defineMessages({
	versionHistoryTitle: {
		id: 'version-history.title',
		defaultMessage: 'History',
		description: 'Title for version history component',
	},
	versionHistoryHeaderTitle: {
		id: 'version-history.header-title',
		defaultMessage: 'Version history',
		description: 'Header title for version history component',
	},
	versionHistoryCloseButtonLabel: {
		id: 'version-history.close.button.icon.label',
		defaultMessage: 'Close',
		description: 'aria-label for close button',
	},
	restoreWhiteboardLabel: {
		id: 'version-history.restore.whiteboard.label',
		defaultMessage: 'Restore as new whiteboard',
		description: 'aria label for restore as new whiteboard button',
	},
	restoreWhiteboardButtonText: {
		id: 'version-history.restore.whiteboard.button.text',
		defaultMessage: 'Restore as new whiteboard',
		description: 'Button text to restore whiteboard',
	},
	restoreDatabaseButtonText: {
		id: 'version-history.restore.database.button.text',
		defaultMessage: 'Restore as new database',
		description: 'Button text to restore database',
	},
	restoreDatabaseLabel: {
		id: 'version-history.restore.database.label',
		defaultMessage: 'Restore as new database',
		description: 'aria label for restore as new whiteboard button',
	},
});

type ContentTypeMapping = {
	[key: string]: { buttonText: MessageDescriptor; label: MessageDescriptor };
};

const contentTypeMessaging: ContentTypeMapping = {
	whiteboard: {
		buttonText: i18n.restoreWhiteboardButtonText,
		label: i18n.restoreWhiteboardLabel,
	},
	database: {
		buttonText: i18n.restoreDatabaseButtonText,
		label: i18n.restoreDatabaseLabel,
	},
};

type VersionHistoryProps = {
	headerHeight?: number;
	contentId: string;
	title: string;
	displayTopBorder?: boolean;
	panelTop?: number;
	eventService: ContentTypeEventService;
	setVersion: (version: VersionHistoryNodeType | null) => void;
	contentType: string;
};

const VersionHistoryComponent = ({
	contentId,
	title,
	headerHeight = 0,
	displayTopBorder,
	panelTop = 0,
	eventService,
	setVersion,
	contentType,
}: VersionHistoryProps) => {
	const { hideSidePanel } = useSidePanelStateActions();
	const { formatMessage } = useIntl();
	const [showRestoreModal, setShowRestoreModal] = useState(false);

	const { onVersionClick, onVersionHistoryMenuOpen, onRestoreModalClose } =
		useVersionHistoryAnalytics();

	const { autosavedVersions, loading, loadMoreVersions, hasNextPage, cursor, error, refetch } =
		useVersionHistory({
			contentId,
			contentType,
		});

	// current version is selected by default
	const [selectedVersion, setSelectedVersion] = useState<VersionHistoryNodeType | null>(null);

	const { trackExperience } = useVersionHistoryExperienceTracking();
	const headerTitle = fg('confluence_frontend_content_wrapper')
		? i18n.versionHistoryHeaderTitle
		: i18n.versionHistoryTitle;

	useEffect(() => {
		// succeed side panel experience on render
		trackExperience({
			contentType,
			experience: 'sidePanel',
			task: 'succeed',
		});
	}, [contentType, trackExperience]);

	useEffect(() => {
		if (!selectedVersion) {
			setSelectedVersion(null);
		}
	}, [setSelectedVersion, selectedVersion]);

	useEffect(() => {
		onVersionHistoryMenuOpen();
	}, [onVersionHistoryMenuOpen]);

	useEffect(() => {
		return () => {
			setVersion(null);
		};
	}, [setVersion]);

	const handleClose = useCallback(() => {
		onRestoreModalClose();
		setShowRestoreModal(false);
	}, [onRestoreModalClose]);

	const handleVersionClick = useCallback(
		(version: VersionHistoryNodeType | null) => {
			setSelectedVersion(version);
			onVersionClick(version);
			setVersion(version || null);
		},
		[onVersionClick, setVersion],
	);

	// current version does not return externalVersionId
	const isRestoreButtonDisabled =
		!!error || !Boolean(selectedVersion?.contentProperties?.latest?.externalVersionId);

	const handleRestoreVersionClick = useCallback(() => {
		trackExperience({
			contentType,
			experience: 'restoreModal',
			task: 'start',
		});
		setShowRestoreModal(true);
	}, [contentType, trackExperience]);

	return (
		<Fragment>
			<SidePanel
				headerHeight={headerHeight}
				displayTopBorder={displayTopBorder}
				panelTop={panelTop}
			>
				{loading ? (
					<VersionHistoryLoadingPlaceholder />
				) : (
					<Fragment>
						<Box
							backgroundColor="elevation.surface"
							paddingInlineStart="space.300"
							paddingInlineEnd="space.150"
							paddingBlock="space.150"
						>
							<Flex direction="row" justifyContent="space-between" alignItems="center">
								<Inline space="space.100" xcss={headerTitleStyles}>
									<ClockIconMigration
										label=""
										color={token('color.icon')}
										spacing="none"
										aria-hidden
									/>
									<Heading size="small" as="h5">
										<FormattedMessage {...headerTitle} />
									</Heading>
								</Inline>
								<IconButton
									appearance="subtle"
									icon={CrossIcon}
									onClick={hideSidePanel}
									label={formatMessage(i18n.versionHistoryCloseButtonLabel)}
								/>
							</Flex>
						</Box>
						<VersionHistoryContent
							autosavedVersions={autosavedVersions}
							hasNextPage={hasNextPage}
							loadMoreVersions={loadMoreVersions}
							cursor={cursor}
							error={error}
							refetch={refetch}
							loading={loading}
							handleVersionClick={handleVersionClick}
							selectedVersion={selectedVersion}
							eventService={eventService}
							contentType={contentType}
							title={title}
						/>
						<SidePanelFooter>
							<Button
								appearance="primary"
								aria-label={formatMessage(contentTypeMessaging[contentType].label)}
								shouldFitContainer
								onClick={handleRestoreVersionClick}
								isDisabled={isRestoreButtonDisabled}
							>
								<FormattedMessage {...contentTypeMessaging[contentType].buttonText} />
							</Button>
						</SidePanelFooter>
					</Fragment>
				)}
			</SidePanel>
			{showRestoreModal && selectedVersion && (
				<RestoreContentModal
					onClose={handleClose}
					selectedVersion={selectedVersion}
					contentType={contentType}
				/>
			)}
		</Fragment>
	);
};

export const VersionHistory = withErrorBoundary({
	attribution: Attribution.CONTENT_TYPES,
})(VersionHistoryComponent);
